import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Bell, Plus, Edit2, Trash2, Power, PowerOff, Mail, AppWindow,
  AlertTriangle, Clock, RefreshCw, CheckCircle2, Info, Repeat
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { apiFetch, API } from "@/utils/api";

export default function Alerts() {
  const [configs, setConfigs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState("config");

  const [form, setForm] = useState({
    alert_type: "invoice_due", name: "", description: "",
    trigger_days_before: 10, trigger_day_of_week: null,
    email_notification: true, app_notification: true
  });

  const fetchConfigs = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/alerts/config`);
      if (res.ok) setConfigs(await res.json());
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/alerts/notifications?limit=100`);
      if (res.ok) setNotifications(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchConfigs(); fetchNotifications(); }, [fetchConfigs, fetchNotifications]);

  const handleSave = async () => {
    const method = editing ? "PUT" : "POST";
    const url = editing ? `${API}/alerts/config/${editing.alert_id}` : `${API}/alerts/config`;
    const payload = { ...form, trigger_days_before: parseInt(form.trigger_days_before) || 10 };
    if (payload.trigger_day_of_week === null || payload.trigger_day_of_week === "") delete payload.trigger_day_of_week;
    else payload.trigger_day_of_week = parseInt(payload.trigger_day_of_week);
    try {
      const res = await apiFetch(url, {
        method,
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(editing ? "Alerta actualizada" : "Alerta creada");
        setShowForm(false);
        setEditing(null);
        fetchConfigs();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error");
      }
    } catch (e) { toast.error("Error de conexion"); }
  };

  const handleToggle = async (alertId) => {
    try {
      const res = await apiFetch(`${API}/alerts/config/${alertId}/toggle`, { method: "PUT" });
      if (res.ok) { fetchConfigs(); }
    } catch (e) { toast.error("Error"); }
  };

  const handleDelete = async (alertId) => {
    if (!window.confirm("Eliminar alerta?")) return;
    try {
      const res = await apiFetch(`${API}/alerts/config/${alertId}`, { method: "DELETE" });
      if (res.ok) { toast.success("Eliminada"); fetchConfigs(); }
    } catch (e) { toast.error("Error"); }
  };

  const handleSetupDefaults = async () => {
    try {
      const res = await apiFetch(`${API}/alerts/setup-defaults`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchConfigs();
      }
    } catch (e) { toast.error("Error"); }
  };

  const handleCheckAlerts = async () => {
    try {
      const res = await apiFetch(`${API}/alerts/check`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.notifications_created} notificaciones generadas`);
        fetchNotifications();
      }
    } catch (e) { toast.error("Error"); }
  };

  const handleMarkRead = async (notifId) => {
    try {
      await apiFetch(`${API}/alerts/notifications/${notifId}/read`, { method: "PUT" });
      fetchNotifications();
    } catch (e) { console.error(e); }
  };

  const handleMarkAllRead = async () => {
    try {
      await apiFetch(`${API}/alerts/notifications/read-all`, { method: "PUT" });
      fetchNotifications();
      toast.success("Todas marcadas como leidas");
    } catch (e) { toast.error("Error"); }
  };

  const typeLabels = {
    invoice_due: "Vencimiento Factura",
    subscription_renewal: "Renovacion Suscripcion",
    recurring_generated: "Factura recurrente generada",
  };

  const typeIcons = {
    invoice_due: <AlertTriangle className="w-4 h-4 text-amber-500" />,
    subscription_renewal: <RefreshCw className="w-4 h-4 text-blue-500" />,
    recurring_generated: <Repeat className="w-4 h-4 text-emerald-500" />,
  };

  const notifColors = {
    warning: "border-l-amber-500 bg-amber-50/50",
    urgent: "border-l-red-500 bg-red-50/50",
    info: "border-l-blue-500 bg-blue-50/50",
  };

  return (
    <div className="space-y-6" data-testid="alerts-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-slate-900 tracking-tight" data-testid="alerts-title">Alertas y Notificaciones</h1>
          <p className="text-sm text-slate-500 mt-1">Configura avisos para vencimientos y renovaciones</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="check-alerts-btn" onClick={handleCheckAlerts}>
            <RefreshCw className="w-4 h-4 mr-2" /> Comprobar Ahora
          </Button>
          <Button
            data-testid="add-alert-btn"
            onClick={() => { setEditing(null); setForm({ alert_type: "invoice_due", name: "", description: "", trigger_days_before: 10, trigger_day_of_week: null, email_notification: true, app_notification: true }); setShowForm(true); }}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Nueva Alerta
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        <button
          data-testid="tab-config"
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "config" ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
          onClick={() => setActiveTab("config")}
        >
          Configuracion ({configs.length})
        </button>
        <button
          data-testid="tab-notifications"
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "notifications" ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
          onClick={() => setActiveTab("notifications")}
        >
          Notificaciones ({notifications.filter(n => !n.is_read).length} sin leer)
        </button>
      </div>

      {/* Config Tab */}
      {activeTab === "config" && (
        <div>
          {loading ? (
            <div className="text-center py-12 text-slate-500">Cargando...</div>
          ) : configs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
              <Bell className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 mb-4">No hay alertas configuradas</p>
              <Button data-testid="setup-defaults-btn" onClick={handleSetupDefaults} variant="outline">
                Configurar alertas por defecto
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {configs.map(c => (
                <div key={c.alert_id} className={`bg-white border rounded-lg p-4 flex items-center justify-between ${!c.is_active ? "opacity-60" : ""}`} data-testid={`alert-config-${c.alert_id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center">
                      {typeIcons[c.alert_type] || <Bell className="w-4 h-4" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-sm text-slate-900">{c.name}</h3>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200">{typeLabels[c.alert_type] || c.alert_type}</span>
                      </div>
                      {c.description && <p className="text-xs text-slate-500 mt-0.5">{c.description}</p>}
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {c.alert_type === "recurring_generated" ? "Al generarse" : `${c.trigger_days_before} dias antes`}
                        </span>
                        {c.email_notification && <span className="text-xs text-slate-400 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</span>}
                        {c.app_notification && <span className="text-xs text-slate-400 flex items-center gap-1"><AppWindow className="w-3 h-3" /> App</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={c.is_active} onCheckedChange={() => handleToggle(c.alert_id)} data-testid={`toggle-alert-${c.alert_id}`} />
                    <button onClick={() => { setEditing(c); setForm({ alert_type: c.alert_type, name: c.name, description: c.description || "", trigger_days_before: c.trigger_days_before, trigger_day_of_week: c.trigger_day_of_week, email_notification: c.email_notification, app_notification: c.app_notification }); setShowForm(true); }} className="p-1.5 rounded hover:bg-slate-100"><Edit2 className="w-3.5 h-3.5 text-slate-500" /></button>
                    <button onClick={() => handleDelete(c.alert_id)} className="p-1.5 rounded hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === "notifications" && (
        <div>
          {notifications.length > 0 && (
            <div className="flex justify-end mb-3">
              <Button variant="ghost" size="sm" data-testid="mark-all-read-btn" onClick={handleMarkAllRead}>
                <CheckCircle2 className="w-4 h-4 mr-1" /> Marcar todas como leidas
              </Button>
            </div>
          )}

          {notifications.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
              <Info className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No hay notificaciones</p>
              <p className="text-xs text-slate-400 mt-1">Pulsa "Comprobar Ahora" para buscar alertas pendientes</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.map(n => (
                <div
                  key={n.notification_id}
                  className={`bg-white border rounded-lg p-4 border-l-4 ${notifColors[n.notification_type] || "border-l-slate-300"} ${n.is_read ? "opacity-60" : ""} cursor-pointer hover:shadow-sm transition-shadow`}
                  data-testid={`notification-${n.notification_id}`}
                  onClick={() => !n.is_read && handleMarkRead(n.notification_id)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-sm text-slate-900">{n.title}</h4>
                      <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-slate-400">{n.created_at?.slice(0, 10)}</span>
                      {!n.is_read && <span className="block w-2 h-2 rounded-full bg-blue-500 ml-auto mt-1" />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="alert-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar Alerta" : "Nueva Alerta"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Tipo de Alerta</label>
              <Select value={form.alert_type} onValueChange={v => setForm({ ...form, alert_type: v })}>
                <SelectTrigger data-testid="alert-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice_due">Vencimiento de Factura</SelectItem>
                  <SelectItem value="subscription_renewal">Renovacion de Suscripcion</SelectItem>
                  <SelectItem value="recurring_generated">Factura recurrente generada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Nombre *</label>
              <Input data-testid="alert-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ej: Facturas por vencer" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Descripcion</label>
              <textarea data-testid="alert-desc-input" className="w-full border border-slate-200 rounded-md p-2 text-sm" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            {form.alert_type === "recurring_generated" ? (
              <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2.5">
                Esta alerta avisa en el momento en que el sistema genera automaticamente una factura recurrente. No usa "dias antes". Elige abajo si quieres el aviso por email, dentro de la app o ambos.
              </p>
            ) : (
              <div>
                <label className="text-sm font-medium mb-1 block">Dias antes del evento</label>
                <Input data-testid="alert-days-input" type="number" value={form.trigger_days_before} onChange={e => setForm({ ...form, trigger_days_before: e.target.value })} />
                {form.alert_type === "invoice_due" && (
                  <p className="text-xs text-slate-400 mt-1">Cubre tambien las cuotas de las facturas con pago fraccionado.</p>
                )}
              </div>
            )}
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.email_notification} onCheckedChange={v => setForm({ ...form, email_notification: v })} data-testid="alert-email-switch" />
                <Mail className="w-4 h-4 text-slate-500" /> Email
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.app_notification} onCheckedChange={v => setForm({ ...form, app_notification: v })} data-testid="alert-app-switch" />
                <AppWindow className="w-4 h-4 text-slate-500" /> App
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button data-testid="save-alert-btn" onClick={handleSave} disabled={!form.name} className="bg-slate-900 hover:bg-slate-800 text-white">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
